import { useRef } from "react";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import { OverviewPageProps } from "../OverviewPage/types";
import { BasicTeamSectionPageProps } from "../BasicTeamSectionPage/types";
import {
  TeamReportDataTableProps,
  TeamReportDataTableRow,
} from "../TeamReportDataTable/types";
import TeamReportDataTable from "../TeamReportDataTable";
import TeamHeaderBand, { BreakdownStrip } from "../OverviewPage/TeamHeaderBand";
import RotationMap from "../OverviewPage/RotationMap";
import PrintHeader from "../PrintHeader";
import PrintPageWrapper from "../../PrintPageWrapper";
import { PACKET_PAGE_COUNT } from "../../util/packetPages";
import { FIRST_SHOT_LABELS, rebTransSideLabels } from "../ShotQualLabel";
import ChallengesTable from "../ChallengesTable";
import type { GameShotProfile, GameShotProfileSide, ReportSourceInfo } from "../types";
import Quadrant from "./Quadrant";
import ShotQualityLabel from "./ShotQualityLabel";
import GameShotBlock, { SeasonShots } from "./GameShotBlock";
import HeadshotStrip from "./HeadshotStrip";
import { useChallengeQuery, useReportReady, useTeamChallengeTotals } from "./hooks";
import {
  CRASH_MIN_CHANCES,
  GAME_SAMPLE_FLOOR,
  gameLine,
  gameScope,
  pickHeadshots,
  seasonLine,
} from "./utils";
import "../teamReportDesign.scss";
import "./ownershipPostgame.scss";

/** Page 2's rotation rows: as many as its fit was measured at (WorstCase, 15 players). */
const OWNERSHIP_ROTATION_MAX = 15;
const playerMinutes = (p: { rotationMinutes?: number[] }) =>
  (p.rotationMinutes ?? []).reduce((a, m) => a + (m || 0), 0);

// Game blobs carry only plain table sections (the sub-table blocks are
// season-only), so the narrowing cast is safe.
const asTable = (x: unknown) => x as TeamReportDataTableProps | undefined;

// Last season's five columns plus Shot Quality (Trevor, decision E), read as
// PPP, xPPP | Shot Quality, TS%, ORB%, TOV% (review 2026-09-30): the group
// rule lands after xPPP because getColumnGroupStarts starts the second group
// at the shot-quality column when present. Blobs built before TR3 label TSQ
// "xTS%", later ones "Shot Qual"; either renders as the stacked Shot Quality head.
const shotQualKey = (headers?: string[]) =>
  headers?.includes("Shot Qual") ? "Shot Qual" : "xTS%";
const overallHeaders = (headers?: string[]) => {
  const sq = shotQualKey(headers);
  return {
    headers: ["PPP", "xPPP", sq, "TS%", "ORB%", "TOV%"],
    headerLabels: { [sq]: ShotQualityLabel },
  };
};
// The first-shot quadrants' overall row (IV1, 2026-10-10): First Shot PPP, First Shot xPPP | Shot Qual (TS%), TS%,
// ORB%, TOV%, per halfcourt possession before its first ORB. Blobs built before it keep the overview's PPP, xPPP pair.
const firstShotHeaders = (headers?: string[]) => {
  if (!headers?.includes("First Shot PPP")) return overallHeaders(headers);
  const sq = shotQualKey(headers);
  return {
    headers: ["First Shot PPP", "First Shot xPPP", sq, "TS%", "ORB%", "TOV%"],
    headerLabels: { ...FIRST_SHOT_LABELS, [sq]: ShotQualityLabel },
  };
};
const REB_TRANS_HEADERS = ["ORB%", "Pts/ORB", "Pts/Miss", "Trans Rate", "Trans PPP"];

const withContext = (
  rows: TeamReportDataTableRow[] | undefined,
  context: (row: TeamReportDataTableRow) => "offense" | "defense",
) => (rows ?? []).map((row) => ({ ...row, shotDistributionContext: context(row) }));

/** A page's group kicker (CONVENTIONS "group kickers divide the page"). */
const Kicker = ({ children }: { children: React.ReactNode }) => (
  <div className="grp">
    <span className="k">{children}</span>
    <span className="bar" />
  </div>
);

/** @page footer (Chrome ≥ 131 page-margin boxes): real page counters, so a
 * page that ever spills to a third sheet says so ("page 3 of 3"). Every
 * margin box is declared, empty where unused: Chrome's print-dialog default
 * "Headers and footers" draws its date / page title / URL into any margin
 * box the page leaves undeclared. Printed as the packet's pages 1-2
 * (`?packet=1`, update/packet/config.PARTS) the total is the packet's. */
const footerCss = (line: string, packet: boolean) => {
  const text = line.toUpperCase().replace(/["\\]/g, "");
  const box =
    'font-family: "Roboto Condensed", sans-serif; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.1em; color: #8a8a8a;';
  return `@media print {
  @page ownership {
    @bottom-left { content: "${text}"; ${box} }
    @bottom-right { content: "PAGE " counter(page) " OF " ${packet ? `"${PACKET_PAGE_COUNT}"` : "counter(pages)"}; ${box} }
    @bottom-center { content: ""; }
    @top-left { content: ""; }
    @top-center { content: ""; }
    @top-right { content: ""; }
  }
}`;
};

export default function OwnershipPostGamePage({
  overviewPageProps,
  halfcourtOffensePageProps,
  halfcourtDefensePageProps,
  transitionOffensePageProps,
  transitionDefensePageProps,
  gameShotProfile,
  teamId,
  season,
  seasonType,
  reportSource,
}: {
  overviewPageProps: OverviewPageProps;
  halfcourtOffensePageProps: BasicTeamSectionPageProps;
  halfcourtDefensePageProps: BasicTeamSectionPageProps;
  transitionOffensePageProps: BasicTeamSectionPageProps;
  transitionDefensePageProps: BasicTeamSectionPageProps;
  gameShotProfile?: GameShotProfile | null;
  /** A season report's shot fetch and challenge totals (team NBA id, season, report season type, provider) */
  teamId?: string;
  season?: string;
  seasonType?: string;
  reportSource?: ReportSourceInfo;
}) {
  const { headerProps, rankingsProps } = overviewPageProps;
  const { gameInfo, game, teamAbbrev } = headerProps;
  // One page, two spans (Trevor 2026-10-27, the packet view): a game blob reads as the postgame report; a season blob
  // as the season's, with ranks, the season hexbin and challenge totals. Every block below is the same for both.
  const single = !!(game || gameInfo);
  const gameChallenges = useChallengeQuery(game?.id ?? game?.game_id, single);
  // A window report's totals are over its games (header.window), a season's over the season
  const reportWindow = headerProps.window ?? null;
  const seasonChallenges = useTeamChallengeTotals(
    teamId,
    teamAbbrev,
    season,
    seasonType,
    !single,
    reportWindow?.gameIds,
  );
  const challenges = single ? gameChallenges : seasonChallenges;
  // The location, not the router: the page also renders outside one (its tests, any embed)
  const packet = new URLSearchParams(window.location.search).get("packet") === "1";

  const root = useRef<HTMLDivElement>(null);
  const { ready, errors } = useReportReady(
    root,
    challenges.status === "loading",
    challenges.status === "error" ? ["challenges"] : [],
  );

  const date = gameInfo?.date ? dayjs(gameInfo.date).format("MMM D, YYYY") : "";
  const product = single ? "Postgame Report" : reportWindow ? `${reportWindow.label} Report` : "Season Report";
  const line = single ? gameLine(gameInfo, date) : seasonLine(headerProps);
  const headLine = single ? line : seasonLine(headerProps, false);
  const floor = single ? GAME_SAMPLE_FLOOR : undefined;
  const seasonShots = (side: BasicTeamSectionPageProps): SeasonShots | undefined =>
    !single && teamId && season
      ? {
          teamNbaId: teamId,
          season: Number(season),
          seasonType,
          profile: side.shotProfile,
          reportSource,
          contextFilters: asTable(side.sections["Overall"])?.rows?.[0]?.queryContext?.filters,
        }
      : undefined;

  const hcO = halfcourtOffensePageProps.sections;
  const hcD = halfcourtDefensePageProps.sections;
  const orbTransD = transitionDefensePageProps.sections;
  const drbTransO = transitionOffensePageProps.sections;

  const overallRow = (table: unknown, context: "offense" | "defense") => {
    const t = asTable(table);
    return t?.rows?.length ? (
      <TeamReportDataTable
        {...t}
        {...firstShotHeaders(t.headers)}
        shotDistributionContext={context}
        hideRowLabels
        perChancePPP
      />
    ) : null;
  };

  // Zone defense (Trevor, 2026-10-14): the coaches' man / zone table's zone row only, possessions against their zone
  // (offense, beside the assisted table) or in ours (defense, under the shot block's zone table) with PPP and xPPP per
  // chance. A game with no zone keeps the row, blank, so the page never reflows. Blobs without the table (built before
  // CTG's zone was wired) show nothing.
  const zoneRow = (table: unknown, label: string) => {
    const t = asTable(table);
    const man = t?.rows?.find((r) => String(r.name).toLowerCase() === "man");
    const zone = t?.rows?.find((r) => String(r.name).toLowerCase() === "zone");
    const base = zone ?? man;
    if (!t?.headers?.length || !base) return null;
    const row: TeamReportDataTableRow = {
      ...base,
      name: label,
      cells: zone ? zone.cells : { Poss: { value: 0 } },
      subRows: undefined,
      subRowsByType: undefined,
    };
    return (
      <div className="zr">
        <TeamReportDataTable
          rows={[row]}
          headers={["Poss", "PPP", "xPPP"]}
          hideShotDistribution
          perChancePPP
          smallSampleFloor={floor}
        />
      </div>
    );
  };

  const shotBlock = (
    side: GameShotProfileSide | undefined,
    seasonSide: SeasonShots | undefined,
    perspective: "offense" | "defense",
    extra?: React.ReactNode,
  ) =>
    side || seasonSide ? (
      <GameShotBlock profile={side} season={seasonSide} perspective={perspective} extra={extra} />
    ) : (
      extra ?? null
    );

  const assisted = asTable(hcO["Assisted v Unassisted"]);
  const assistedTable = assisted?.rows?.length ? (
    <TeamReportDataTable
      {...assisted}
      headers={["% of Shots", "PPP", "xPPP", "TS%"]}
      hideShotDistribution
      perChancePPP
    />
  ) : null;

  // The quadrants' tables read per chance, as the coaches' phase pages do; only the overview stays per 100
  // possessions (Trevor, 2026-10-10: the page had drifted to per 100 everywhere)
  const rebTrans = (
    sections: Record<string, unknown>,
    side: "orb" | "drb",
    strip: React.ReactNode,
  ) => {
    const overall = asTable(sections["Overall"]);
    const byType = asTable(sections["Transition by type of possession"]);
    return (
      <div className="rt">
        <div>
          {overall?.rows?.length ? (
            <TeamReportDataTable
              {...overall}
              headers={REB_TRANS_HEADERS}
              headerLabels={rebTransSideLabels(side)}
              hideShotDistribution
              hideRowLabels
              perChancePPP
            />
          ) : null}
          {strip}
        </div>
        <div>
          {byType?.rows?.length ? (
            <TeamReportDataTable
              {...byType}
              headers={["Trans Rate", "Poss", "PPP", "xPPP"]}
              headerLabels={rebTransSideLabels(side)}
              hideShotDistribution
              compactRowLabels
              smallSampleFloor={floor}
              perChancePPP
            />
          ) : null}
        </div>
      </div>
    );
  };

  // Crash rate on threes against the position group's median, the packet's rebounding page's standard (IV1,
  // 2026-10-10; the coaches' report keeps the smoothed positional expectation). Blobs built before it carry only
  // the positional OE, labelled "Crash Rate Over Positional Expectation" since RV1 (2026-10-16). Ranked by the
  // printed value: the cell's percentiles rank within each group, so they don't order a guard against a big
  // (Trevor, 2026-10-11).
  const crashFrom = (metric: string) =>
    pickHeadshots(asTable(orbTransD["Individual Rebounding"]), metric, CRASH_MIN_CHANCES, "value");
  const crashVsGroup = crashFrom("Crash Rate vs Group Median");
  const crash = [
    crashVsGroup,
    crashFrom("Crash Rate Over Positional Expectation"),
    crashFrom("Crash Rate Over Expectation"),
  ].find((players) => players.length) ?? [];
  const crashHint = crashVsGroup.length
    ? "Crash rate on threes minus the median of the player's position group (guards, wings, bigs), as on the rebounding page"
    : undefined;
  // His man's offensive rebounds over expectation (RV1, 2026-10-16); blobs built before it carry the ORB-conceded
  // model's version of the same read. The percentile is oriented higher = better (fewer allowed).
  const orbAllowedFrom = (metric: string) =>
    pickHeadshots(asTable(drbTransO["Individual Rebounding"]), metric);
  const rv1OrbAllowed = orbAllowedFrom("ORB Allowed +/-");
  const orbAllowed = rv1OrbAllowed.length
    ? rv1OrbAllowed
    : orbAllowedFrom("ORB Conceded Over Expectation");
  // Page 2 fits a 15-man rotation (the WorstCase story); a window or a long season can list more. Most minutes first.
  const rotation = [...overviewPageProps.playerRotations.players].sort(
    (a, b) => playerMinutes(b) - playerMinutes(a),
  );

  return (
    <Box
      ref={root}
      className="OwnershipPostGame trd ogp"
      data-report-ready={ready ? "true" : "false"}
      data-report-errors={errors.length ? errors.join(",") : undefined}
    >
      <style>{footerCss(line, packet)}</style>
      <PrintPageWrapper id="ownership-p1">
        <TeamHeaderBand
          headerProps={headerProps}
          rankingsProps={rankingsProps}
          isSingleGame={single}
          scope={single ? gameScope(game) : undefined}
          productLabel={product}
          // A game's date, not the blob's build date; a season keeps "As of ..."
          stamp={date || undefined}
          showBreakdown={false}
          // a delivered packet carries no Preliminary tag (Trevor decides whether a delayed-feed one goes out)
          showPreliminary={false}
          reportLabel={
            gameInfo
              ? `${gameInfo.isHome ? "vs" : "at"} ${gameInfo.opponent}`
              : single
                ? "Game Report"
                : "Ownership"
          }
        />
        <div className="sh pg">
          <span className="ttl">Overview</span>
          <span className="nt">
            <ChallengesTable
              inline
              teams={(single ? [teamAbbrev, gameInfo?.opponent] : [teamAbbrev, "OPP"]).filter(Boolean) as string[]}
              status={challenges.status}
              challenges={challenges.status === "ok" ? challenges.data : undefined}
            />
          </span>
        </div>
        <TeamReportDataTable
          {...overviewPageProps.teamOverviewTableData}
          {...overallHeaders(overviewPageProps.teamOverviewTableData.headers)}
          rows={withContext(
            overviewPageProps.teamOverviewTableData.rows,
            (row) =>
              typeof row.name === "string" &&
              row.name.toLowerCase().includes("offense")
                ? "offense"
                : "defense",
          )}
        />
        {/* The four quadrants' game percentiles: page 1 stands alone for a
            reader who never turns the page */}
        <BreakdownStrip rankingsProps={rankingsProps} isSingleGame={single} />
        <Kicker>When we had the ball</Kicker>
        <Quadrant
          id="hc-offense"
          title="First-Shot Halfcourt Offense"
          ranking={rankingsProps.hcOffense}
          isSingleGame={single}
        >
          {overallRow(hcO["Overall"], "offense")}
          {shotBlock(gameShotProfile?.offense, seasonShots(halfcourtOffensePageProps), "offense")}
          {/* beside the assisted table, which is taller: page 1's height is fixed with little slack */}
          <div className="az">
            {assistedTable}
            {zoneRow(hcO["Vs Zone"], "Vs zone")}
          </div>
        </Quadrant>
        <Quadrant
          id="orb-trans-d"
          title="Offensive Rebounding + Transition Defense"
          ranking={rankingsProps.trDefense}
          isSingleGame={single}
        >
          {rebTrans(
            orbTransD,
            "orb",
            crash.length > 0 && (
              <HeadshotStrip
                title="Crash Rate vs Positional Expectation"
                hint={crashHint}
                players={crash}
              />
            ),
          )}
        </Quadrant>
      </PrintPageWrapper>
      <PrintPageWrapper
        id="ownership-p2"
        printHeader={
          <PrintHeader
            teamId={headerProps.teamId}
            teamName={headerProps.teamName}
            reportLabel={product}
            contextLabel={headLine}
            rankPopulation={rankingsProps.hcOffense.rankPopulation}
            legendInline
          />
        }
      >
        <Kicker>When they had the ball</Kicker>
        <Quadrant
          id="hc-defense"
          title="First-Shot Halfcourt Defense"
          ranking={rankingsProps.hcDefense}
          isSingleGame={single}
        >
          {overallRow(hcD["Overall"], "defense")}
          {shotBlock(
            gameShotProfile?.defense,
            seasonShots(halfcourtDefensePageProps),
            "defense",
            zoneRow(hcD["Zone"], "Our zone"),
          )}
        </Quadrant>
        <Quadrant
          id="drb-trans-o"
          title="Defensive Rebounding + Transition Offense"
          ranking={rankingsProps.trOffense}
          isSingleGame={single}
        >
          {rebTrans(
            drbTransO,
            "drb",
            // CTG has no rebound-probability model yet (decision C): the strip
            // returns, with its original meaning, when the blob carries values.
            // Until then its slot stays reserved so the page doesn't reflow.
            orbAllowed.length > 0 ? (
              <HeadshotStrip
                title="ORB Allowed +/-"
                hint="Offensive rebounds by the man he was matched up with, compared to expected: positive means he gave up more than expected"
                players={orbAllowed}
              />
            ) : (
              <div className="hs hs-slot" aria-hidden />
            ),
          )}
        </Quadrant>
        {/* Garbage time is the coaches' report's, not ownership's (Trevor, 2026-10-10) */}
        <RotationMap
          {...overviewPageProps.playerRotations}
          players={rotation.slice(0, OWNERSHIP_ROTATION_MAX)}
          isSingleGame={single}
        />
        {rotation.length > OWNERSHIP_ROTATION_MAX && (
          <div className="fn">
            The {OWNERSHIP_ROTATION_MAX} players with the most minutes;{" "}
            {rotation.length - OWNERSHIP_ROTATION_MAX} more played.
          </div>
        )}
      </PrintPageWrapper>
    </Box>
  );
}
